/** Famille d'icônes Python Learning — un seul jeu SVG cohérent.
 *  Recette commune : viewBox 24×24, stroke="currentColor", stroke-width 2,
 *  caps/joins arrondis. Les icônes héritent donc de la couleur du texte
 *  (`color`) et s'alignent sur la taille de police environnante.
 *  Remplace les emoji et les glyphes Unicode utilisés auparavant. */

export type IconName =
  | "run"
  | "stop"
  | "more"
  | "running"
  | "clear"
  | "download"
  | "upload"
  | "back"
  | "next"
  | "collapsed"
  | "expanded"
  | "status"
  | "done"
  | "locked"
  | "file"
  | "light"
  | "dark"
  | "panel"
  | "trash"
  | "up"
  | "down"
  | "home"
  | "rename"
  | "duplicate"
  | "search"
  | "first"
  | "last"
  | "alert"
  | "terminal"
  | "checklist"
  | "archive"
  | "eye"
  | "eye-off";

/* Corps de chaque icône (tracé interne uniquement) — repris 1:1 des assets
   du design system (assets/icons/<name>.svg). */
const PATHS: Record<IconName, React.ReactNode> = {
  run: <polygon points="6,4 20,12 6,20" fill="currentColor" stroke="none" />,
  stop: <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" />,
  more: (
    <>
      <circle cx="5" cy="12" r="1.7" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.7" fill="currentColor" stroke="none" />
    </>
  ),
  running: (
    <>
      <path d="M7 4 h10" />
      <path d="M7 20 h10" />
      <path d="M7 4 v3 a5 5 0 0 0 10 0 V4" />
      <path d="M7 20 v-3 a5 5 0 0 1 10 0 v3" />
    </>
  ),
  clear: (
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  ),
  download: (
    <>
      <path d="M12 4 v11" />
      <polyline points="7,11 12,16 17,11" />
      <line x1="5" y1="20" x2="19" y2="20" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16 V5" />
      <polyline points="7,9 12,4 17,9" />
      <line x1="5" y1="20" x2="19" y2="20" />
    </>
  ),
  back: (
    <>
      <line x1="20" y1="12" x2="4" y2="12" />
      <polyline points="9,6 4,12 9,18" />
    </>
  ),
  next: (
    <>
      <line x1="4" y1="12" x2="20" y2="12" />
      <polyline points="15,6 20,12 15,18" />
    </>
  ),
  collapsed: <polyline points="9,6 15,12 9,18" />,
  expanded: <polyline points="6,9 12,15 18,9" />,
  status: <circle cx="12" cy="12" r="5" fill="currentColor" stroke="none" />,
  done: <polyline points="5,12 10,17 19,7" />,
  locked: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11 V8 a4 4 0 0 1 8 0 v3" />
    </>
  ),
  file: (
    <path d="M17 7 l-7 7 a3 3 0 0 0 4.2 4.2 l8.2 -8.2 a5 5 0 0 0 -7 -7 l-9 9 a7 7 0 0 0 10 10 l6 -6" />
  ),
  light: (
    <>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4.5" />
      <line x1="12" y1="19.5" x2="12" y2="22" />
      <line x1="2" y1="12" x2="4.5" y2="12" />
      <line x1="19.5" y1="12" x2="22" y2="12" />
      <line x1="4.5" y1="4.5" x2="6.3" y2="6.3" />
      <line x1="17.7" y1="17.7" x2="19.5" y2="19.5" />
      <line x1="17.7" y1="6.3" x2="19.5" y2="4.5" />
      <line x1="4.5" y1="19.5" x2="6.3" y2="17.7" />
    </>
  ),
  dark: <path d="M20 14.5 A8 8 0 1 1 9.5 4 a6 6 0 0 0 10.5 10.5 Z" />,
  panel: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </>
  ),
  trash: (
    <>
      <polyline points="4,7 20,7" />
      <path d="M9 7 V5 a1 1 0 0 1 1 -1 h4 a1 1 0 0 1 1 1 v2" />
      <path d="M6 7 l1 13 a1 1 0 0 0 1 1 h8 a1 1 0 0 0 1 -1 l1 -13" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </>
  ),
  up: <polyline points="6,15 12,9 18,15" />,
  down: <polyline points="6,9 12,15 18,9" />,
  home: (
    <>
      <path d="M3 11 l9 -8 l9 8" />
      <path d="M5 9.5 V20 a1 1 0 0 0 1 1 h12 a1 1 0 0 0 1 -1 V9.5" />
    </>
  ),
  // Renommer : deux panneaux encadrant un curseur texte (I-beam) central.
  rename: (
    <>
      <path d="M9 5 H6 a3 3 0 0 0 -3 3 v8 a3 3 0 0 0 3 3 h3" />
      <path d="M15 5 h3 a3 3 0 0 1 3 3 v8 a3 3 0 0 1 -3 3 h-3" />
      <line x1="12" y1="3.5" x2="12" y2="20.5" />
      <line x1="9.5" y1="3.5" x2="14.5" y2="3.5" />
      <line x1="9.5" y1="20.5" x2="14.5" y2="20.5" />
    </>
  ),
  // Aller au début / à la fin (barre + chevron) — timeline du visualiseur.
  first: (
    <>
      <line x1="7" y1="5" x2="7" y2="19" />
      <polyline points="18,6 11,12 18,18" />
    </>
  ),
  last: (
    <>
      <line x1="17" y1="5" x2="17" y2="19" />
      <polyline points="6,6 13,12 6,18" />
    </>
  ),
  // Loupe — « Visualiser / inspecter » l'exécution.
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="21" y2="21" />
    </>
  ),
  // Alerte : triangle + point d'exclamation (remplace l'emoji ⚠️).
  alert: (
    <>
      <path d="M12 3.5 L21.5 20 H2.5 Z" />
      <line x1="12" y1="10" x2="12" y2="14.5" />
      <circle cx="12" cy="17.4" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),
  // Console / éditeur libre : fenêtre de terminal avec invite « >_ ».
  terminal: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <polyline points="7,10 10,13 7,16" />
      <line x1="12.5" y1="16" x2="16.5" y2="16" />
    </>
  ),
  // Séries d'exercices : liste à cocher (étapes validées).
  checklist: (
    <>
      <polyline points="3.5,7 5,8.6 7.8,5.4" />
      <line x1="11" y1="7" x2="20.5" y2="7" />
      <polyline points="3.5,14 5,15.6 7.8,12.4" />
      <line x1="11" y1="14" x2="20.5" y2="14" />
    </>
  ),
  // Dupliquer : document à coin replié + document en arrière-plan (copie).
  duplicate: (
    <>
      <path d="M8.5 2.5 H15.5 L19.5 6.5 V17 a1.8 1.8 0 0 1 -1.8 1.8 H8.5 a1.8 1.8 0 0 1 -1.8 -1.8 V4.3 a1.8 1.8 0 0 1 1.8 -1.8 Z" />
      <polyline points="15.5,2.5 15.5,6.5 19.5,6.5" />
      <path d="M5 7.5 A1.8 1.8 0 0 0 3.2 9.3 V19.7 A1.8 1.8 0 0 0 5 21.5 H14.2 A1.8 1.8 0 0 0 16 19.7 V18.8" />
    </>
  ),
  // Archiver : couvercle + boîte avec encoche (≠ poubelle = pas de suppression).
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8 v10 a1.5 1.5 0 0 0 1.5 1.5 h11 a1.5 1.5 0 0 0 1.5 -1.5 V8" />
      <path d="M10 12 h4" />
    </>
  ),
  // Visible : œil ouvert (afficher aux élèves).
  eye: (
    <>
      <path d="M1.5 12 S5.5 5 12 5 s10.5 7 10.5 7 -4 7 -10.5 7 S1.5 12 1.5 12 Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // Masqué : œil barré (caché des élèves).
  "eye-off": (
    <>
      <path d="M9.9 4.9 A10.4 10.4 0 0 1 12 4.7 c6.5 0 10.5 7 10.5 7 a17.6 17.6 0 0 1 -3.3 3.9" />
      <path d="M6.3 6.8 A17.4 17.4 0 0 0 1.5 11.7 s4 7 10.5 7 a10.2 10.2 0 0 0 4.2 -0.9" />
      <path d="M9.9 9.9 a3 3 0 0 0 4.2 4.2" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </>
  ),
};

interface Props {
  name: IconName;
  size?: number;
  style?: React.CSSProperties;
}

export default function Icon({ name, size = 16, style }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0, display: "block", ...style }}
    >
      {PATHS[name]}
    </svg>
  );
}

/* Couleurs Catppuccin du « tournesol » — même marche chromatique que la
   marque de sortie graphique du design system. */
const PETAL_COLORS = [
  "#89b4fa", "#74c7ec", "#89dceb", "#94e2d5", "#a6e3a1", "#f9e2af",
  "#fab387", "#f38ba8", "#eba0ac", "#f5c2e7", "#cba6f7", "#b4befe",
];

/** Marque « sortie graphique » — fleur multicolore (turtle, matplotlib, …).
 *  Seule icône multicolore du système : c'est un élément d'identité, pas un
 *  pictogramme monochrome, donc il n'hérite pas de currentColor. */
export function GraphicIcon({ size = 18, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label="Sortie graphique"
      style={{ flexShrink: 0, display: "block", ...style }}
    >
      {PETAL_COLORS.map((color, i) => (
        <path
          key={i}
          d="M 12 1 C 15 4, 15 8, 12 11 C 9 8, 9 4, 12 1 Z"
          fill={color}
          transform={`rotate(${i * 30} 12 12)`}
        />
      ))}
      <circle cx="12" cy="12" r="1.6" fill="#11111b" />
    </svg>
  );
}
